import type { Candle } from "../types";

export interface OrderBlock {
  time: number;
  type: "BULLISH" | "BEARISH";
  high: number;
  low: number;
}

export interface FairValueGap {
  time: number;
  type: "BULLISH" | "BEARISH";
  top: number;
  bottom: number;
}

export interface SwingPoint {
  time: number;
  price: number;
  index: number;
}

/**
 * Lightweight SMC (Smart Money Concepts) detector — pure TypeScript,
 * operates on in-memory candle history (browser-safe).
 */
export const smc = {
  /**
   * Last Order Block: a supply/demand candle followed by an impulsive
   * displacement candle that closes beyond its extreme.
   */
  orderBlock(candles: Candle[], lookback = 30): OrderBlock | null {
    const start = Math.max(1, candles.length - lookback);
    for (let i = candles.length - 2; i >= start; i--) {
      const ob = candles[i];
      const next = candles[i + 1];

      // Bullish OB: bearish candle engulfed by a strong bullish displacement
      if (
        ob.close < ob.open &&
        next.close > next.open &&
        next.close > ob.high
      ) {
        return { time: ob.time, type: "BULLISH", high: ob.high, low: ob.low };
      }
      // Bearish OB: bullish candle rejected by a strong bearish displacement
      if (
        ob.close > ob.open &&
        next.close < next.open &&
        next.close < ob.low
      ) {
        return { time: ob.time, type: "BEARISH", high: ob.high, low: ob.low };
      }
    }
    return null;
  },

  /** Last Fair Value Gap (3-candle imbalance) */
  fvg(candles: Candle[], lookback = 30): FairValueGap | null {
    const start = Math.max(2, candles.length - lookback);
    for (let i = candles.length - 1; i >= start; i--) {
      const prev = candles[i - 1];
      const next = candles[i + 1];
      if (!next) continue;

      // Bullish FVG: gap between prior high and next low
      if (next.low > prev.high) {
        return { time: candles[i].time, type: "BULLISH", top: next.low, bottom: prev.high };
      }
      // Bearish FVG: gap between prior low and next high
      if (next.high < prev.low) {
        return { time: candles[i].time, type: "BEARISH", top: prev.low, bottom: next.high };
      }
    }
    return null;
  },

  /** Most recent swing highs (peaks) using a fractal lookback */
  swingHighs(candles: Candle[], lookback = 5): SwingPoint[] {
    const result: SwingPoint[] = [];
    for (let i = lookback; i < candles.length - lookback; i++) {
      let isHigh = true;
      for (let j = i - lookback; j <= i + lookback; j++) {
        if (j !== i && candles[j].high >= candles[i].high) {
          isHigh = false;
          break;
        }
      }
      if (isHigh) result.push({ time: candles[i].time, price: candles[i].high, index: i });
    }
    return result;
  },

  /** Most recent swing lows (troughs) using a fractal lookback */
  swingLows(candles: Candle[], lookback = 5): SwingPoint[] {
    const result: SwingPoint[] = [];
    for (let i = lookback; i < candles.length - lookback; i++) {
      let isLow = true;
      for (let j = i - lookback; j <= i + lookback; j++) {
        if (j !== i && candles[j].low <= candles[i].low) {
          isLow = false;
          break;
        }
      }
      if (isLow) result.push({ time: candles[i].time, price: candles[i].low, index: i });
    }
    return result;
  },
};
